import { runPendingDraftSave } from "$/lib/pending-draft-save"
import { useRouter } from "@tanstack/react-router"
import { useEffect, useRef } from "react"

const POLL_INTERVAL_MS = 5 * 60 * 1000

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?_t=${Date.now()}`)
    const data: { version: string } = await response.json()
    return data.version ?? null
  } catch {
    return null
  }
}

export function VersionGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const newVersionAvailable = useRef(false)

  useEffect(() => {
    if (__APP_VERSION__ === "development") {
      return
    }

    const checkVersion = async () => {
      const latestVersion = await fetchLatestVersion()
      if (latestVersion && latestVersion !== __APP_VERSION__) {
        if (!newVersionAvailable.current) {
          console.info(
            "[VersionGuard] New version detected, reloading on next navigation",
          )
        }
        newVersionAvailable.current = true
      }
    }

    checkVersion()

    const interval = setInterval(checkVersion, POLL_INTERVAL_MS)

    const handleFocus = () => {
      checkVersion()
    }
    window.addEventListener("focus", handleFocus)

    return () => {
      clearInterval(interval)
      window.removeEventListener("focus", handleFocus)
    }
  }, [])

  useEffect(() => {
    if (__APP_VERSION__ === "development") {
      return
    }

    const unsubscribe = router.subscribe("onBeforeNavigate", async (event) => {
      if (newVersionAvailable.current && event.hrefChanged) {
        await runPendingDraftSave()
        window.location.href = event.toLocation.href
      }
    })

    return unsubscribe
  }, [router])

  return <>{children}</>
}
